export type QuestionType = 'single' | 'multiple' | 'rating' | 'text';

export interface Question {
  id: string;
  text: string;
  type: QuestionType;
  options?: string[];   // for single/multiple
  maxRating?: number;   // for rating (default 5)
  required: boolean;
}

export interface Survey {
  id: string;
  title: string;
  description: string;
  category: string;
  city: string;
  createdBy: string;    // user email / display name
  createdAt: string;
  closedAt: string | null;
  questions: Question[];
  responseCount: number;
}

export interface SurveyResponse {
  id: string;
  surveyId: string;
  respondedAt: string;
  answers: Record<string, string | string[] | number>;
}

const SURVEYS_KEY = 'city_surveys';
const RESPONSES_KEY = 'survey_responses';
const VOTED_KEY = 'survey_voted';

// ── Seed data ──────────────────────────────────────────────────────────────

const SEED: Survey[] = [
  {
    id: 'seed-1',
    title: 'Urban Mobility & Transport',
    description: 'Help us understand how residents get around the city and what improvements they want.',
    category: 'Transport',
    city: 'Bangkok',
    createdBy: 'City Planning Office',
    createdAt: '2026-04-01T08:00:00Z',
    closedAt: null,
    responseCount: 142,
    questions: [
      { id: 'q1', text: 'What is your primary mode of transport?', type: 'single', required: true,
        options: ['BTS/MRT', 'Motorbike taxi', 'Private car', 'Walking', 'Bicycle', 'Bus', 'Other'] },
      { id: 'q2', text: 'How satisfied are you with public transport?', type: 'rating', maxRating: 5, required: true },
      { id: 'q3', text: 'Which improvements would you prioritise?', type: 'multiple', required: false,
        options: ['More BTS/MRT lines', 'Better bus network', 'Safe cycling lanes', 'Sidewalk improvements', 'Cheaper fares', 'EV infrastructure'] },
      { id: 'q4', text: 'Any other comments about urban mobility?', type: 'text', required: false },
    ],
  },
  {
    id: 'seed-2',
    title: 'Local Food & Farmers Market',
    description: 'We want to know more about community interest in local produce and farmers markets.',
    category: 'Food & Agriculture',
    city: 'Bangkok',
    createdBy: 'UrbanLife Community',
    createdAt: '2026-04-15T10:00:00Z',
    closedAt: null,
    responseCount: 89,
    questions: [
      { id: 'q1', text: 'How often do you buy from local farmers or markets?', type: 'single', required: true,
        options: ['Daily', 'Weekly', 'Monthly', 'Rarely', 'Never'] },
      { id: 'q2', text: 'What matters most when buying local food?', type: 'multiple', required: true,
        options: ['Price', 'Freshness', 'Organic/natural', 'Proximity', 'Supporting farmers', 'Reduced packaging'] },
      { id: 'q3', text: 'Rate the importance of having a weekly farmers market in your neighbourhood', type: 'rating', maxRating: 5, required: true },
      { id: 'q4', text: 'What products would you most like to see?', type: 'text', required: false },
    ],
  },
  {
    id: 'seed-3',
    title: 'Green Spaces & Urban Nature',
    description: 'Share your views on parks, trees, and nature access in the city.',
    category: 'Environment',
    city: 'Bangkok',
    createdBy: 'Green City Initiative',
    createdAt: '2026-05-01T09:00:00Z',
    closedAt: null,
    responseCount: 57,
    questions: [
      { id: 'q1', text: 'How satisfied are you with the amount of green space near you?', type: 'rating', maxRating: 5, required: true },
      { id: 'q2', text: 'How often do you visit parks or green spaces?', type: 'single', required: true,
        options: ['Daily', '2–3 times per week', 'Weekly', 'Monthly', 'Rarely'] },
      { id: 'q3', text: 'What green initiatives should the city prioritise?', type: 'multiple', required: false,
        options: ['More street trees', 'Rooftop gardens', 'Community vegetable plots', 'Urban wetlands', 'Larger parks', 'Wildlife corridors'] },
      { id: 'q4', text: 'Describe your ideal urban green space', type: 'text', required: false },
    ],
  },
];

// ── Helpers ────────────────────────────────────────────────────────────────

export function loadSurveys(): Survey[] {
  try {
    const stored = JSON.parse(localStorage.getItem(SURVEYS_KEY) ?? '[]') as Survey[];
    const storedIds = new Set(stored.map(s => s.id));
    const missing = SEED.filter(s => !storedIds.has(s.id));
    return [...missing, ...stored];
  } catch { return SEED; }
}

export function saveSurveys(list: Survey[]) {
  const userCreated = list.filter(s => !s.id.startsWith('seed-'));
  localStorage.setItem(SURVEYS_KEY, JSON.stringify(userCreated));
}

export function createSurvey(data: Omit<Survey, 'id' | 'createdAt' | 'responseCount'>): Survey {
  const s: Survey = { ...data, id: crypto.randomUUID(), createdAt: new Date().toISOString(), responseCount: 0 };
  const all = loadSurveys();
  saveSurveys([s, ...all.filter(x => !x.id.startsWith('seed-'))]);
  return s;
}

export function loadResponses(): SurveyResponse[] {
  try { return JSON.parse(localStorage.getItem(RESPONSES_KEY) ?? '[]'); } catch { return []; }
}

export function submitResponse(surveyId: string, answers: Record<string, string | string[] | number>): SurveyResponse {
  const r: SurveyResponse = { id: crypto.randomUUID(), surveyId, respondedAt: new Date().toISOString(), answers };
  const all = loadResponses();
  localStorage.setItem(RESPONSES_KEY, JSON.stringify([...all, r]));
  markVoted(surveyId);
  // bump responseCount on user surveys
  const surveys = loadSurveys();
  saveSurveys(surveys.map(s => s.id === surveyId ? { ...s, responseCount: s.responseCount + 1 } : s));
  return r;
}

export function hasVoted(surveyId: string): boolean {
  try { return (JSON.parse(localStorage.getItem(VOTED_KEY) ?? '[]') as string[]).includes(surveyId); }
  catch { return false; }
}

function markVoted(surveyId: string) {
  try {
    const voted = JSON.parse(localStorage.getItem(VOTED_KEY) ?? '[]') as string[];
    localStorage.setItem(VOTED_KEY, JSON.stringify([...voted, surveyId]));
  } catch { /* ignore */ }
}

export function getResultsForSurvey(survey: Survey): Record<string, Record<string, number>> {
  const responses = loadResponses().filter(r => r.surveyId === survey.id);
  const results: Record<string, Record<string, number>> = {};
  for (const q of survey.questions) {
    results[q.id] = {};
    for (const r of responses) {
      const ans = r.answers[q.id];
      if (ans === undefined || ans === '') continue;
      if (Array.isArray(ans)) {
        for (const a of ans) results[q.id][a] = (results[q.id][a] ?? 0) + 1;
      } else {
        const key = String(ans);
        results[q.id][key] = (results[q.id][key] ?? 0) + 1;
      }
    }
  }
  return results;
}

// ── City stats seed data ──────────────────────────────────────────────────

export interface CityStat {
  label: string;
  value: number;
  unit: string;
  trend: 'up' | 'down' | 'flat';
  trendValue: string;
  category: string;
  icon: string;
}

export const CITY_STATS: CityStat[] = [
  { label: 'Air Quality Index', value: 68, unit: 'AQI', trend: 'down', trendValue: '−5 vs last month', category: 'Environment', icon: 'air' },
  { label: 'Green Space per Capita', value: 3.2, unit: 'm²/person', trend: 'up', trendValue: '+0.3 this year', category: 'Environment', icon: 'park' },
  { label: 'Active Community Markets', value: 14, unit: 'markets', trend: 'up', trendValue: '+2 new this quarter', category: 'Food', icon: 'storefront' },
  { label: 'Local Farmers Registered', value: 284, unit: 'farmers', trend: 'up', trendValue: '+22 this year', category: 'Food', icon: 'agriculture' },
  { label: 'Avg Commute Time', value: 52, unit: 'min', trend: 'down', trendValue: '−3 min vs 2025', category: 'Transport', icon: 'commute' },
  { label: 'Cycling Lane Coverage', value: 8.4, unit: 'km', trend: 'up', trendValue: '+1.2km new lanes', category: 'Transport', icon: 'directions_bike' },
  { label: 'Community Events (YTD)', value: 37, unit: 'events', trend: 'up', trendValue: '+12 vs same period 2025', category: 'Community', icon: 'event' },
  { label: 'Urban Gardens', value: 21, unit: 'gardens', trend: 'up', trendValue: '+4 new sites', category: 'Environment', icon: 'local_florist' },
  { label: 'Resident Satisfaction', value: 72, unit: '%', trend: 'up', trendValue: '+4pts this year', category: 'Community', icon: 'sentiment_satisfied' },
];
